import { motion } from "framer-motion";
import { Rocket, Search, Shield, TrendingUp } from "lucide-react";
import MovingBackground from "./MovingBackground";

const ease = [0.16, 1, 0.3, 1] as const;

const phases = [
  { icon: Search, phase: "Phase 1", title: "Discovery & Assessment", duration: "Week 1–2", items: ["Environment audit", "Readiness scoring", "Stakeholder mapping"], color: "hsl(var(--primary))" },
  { icon: Shield, phase: "Phase 2", title: "Governance Framework", duration: "Week 3–5", items: ["Policy creation", "Compliance setup", "Role definitions"], color: "hsl(var(--success))" },
  { icon: Rocket, phase: "Phase 3", title: "Pilot & Deploy", duration: "Week 6–9", items: ["Copilot rollout", "Agent configuration", "User training"], color: "hsl(var(--primary-glow))" },
  { icon: TrendingUp, phase: "Phase 4", title: "Scale & Optimize", duration: "Week 10+", items: ["Adoption tracking", "ROI measurement", "Continuous tuning"], color: "hsl(var(--primary))" },
];

const TimelineSlide = ({ thumbnail }: { thumbnail?: boolean }) => (
  <div className="relative w-full h-full flex flex-col items-center justify-center bg-background overflow-hidden">
    <MovingBackground variant="particles" />

    <div className="relative z-10 w-full max-w-5xl px-8">
      <motion.div
        className="text-center mb-8 md:mb-12"
        initial={thumbnail ? false : { opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, ease }}
      >
        <p className="label-text mb-2" style={{ fontSize: thumbnail ? "0.5rem" : undefined }}>
          Implementation Roadmap
        </p>
        <h2
          className="font-display font-bold text-foreground"
          style={{ fontSize: thumbnail ? "0.9rem" : "clamp(1.5rem, 3vw, 2.5rem)" }}
        >
          Your Path to <span className="text-gradient">AI Excellence</span>
        </h2>
      </motion.div>

      <div className="relative">
        {/* Timeline line */}
        {!thumbnail && (
          <motion.div
            className="absolute left-1/2 top-0 bottom-0 w-px -translate-x-1/2 hidden md:block"
            style={{ background: "var(--gradient-primary)" }}
            initial={{ scaleY: 0 }}
            animate={{ scaleY: 1 }}
            transition={{ duration: 1, ease }}
          />
        )}

        <div className="grid md:grid-cols-2 gap-4 md:gap-8">
          {phases.map((phase, i) => (
            <motion.div
              key={phase.phase}
              className={`relative ${i % 2 === 1 ? "md:mt-16" : ""}`}
              initial={thumbnail ? false : { opacity: 0, x: i % 2 === 0 ? -40 : 40 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.2 + i * 0.15, duration: 0.6, ease }}
            >
              <div className="glow-border bg-card/80 backdrop-blur-sm rounded-xl p-4 md:p-6 border border-border/50">
                <div className="flex items-center gap-3 mb-3">
                  <div
                    className="w-9 h-9 rounded-lg flex items-center justify-center"
                    style={{ background: `${phase.color}15` }}
                  >
                    <phase.icon size={thumbnail ? 10 : 18} style={{ color: phase.color }} />
                  </div>
                  <div>
                    <p className="text-primary text-xs font-semibold font-display">{phase.phase}</p>
                    <h3
                      className="font-display font-semibold text-foreground"
                      style={{ fontSize: thumbnail ? "0.5rem" : "clamp(0.85rem, 1.2vw, 1.1rem)" }}
                    >
                      {phase.title}
                    </h3>
                  </div>
                </div>

                {!thumbnail && (
                  <>
                    <p className="text-xs text-muted-foreground mb-2">{phase.duration}</p>
                    <ul className="space-y-1.5">
                      {phase.items.map((item, j) => (
                        <motion.li
                          key={item}
                          className="flex items-center gap-2 text-sm text-muted-foreground"
                          initial={{ opacity: 0, x: -10 }}
                          animate={{ opacity: 1, x: 0 }}
                          transition={{ delay: 0.5 + i * 0.15 + j * 0.08, duration: 0.3, ease }}
                        >
                          <div className="w-1.5 h-1.5 rounded-full bg-primary/60" />
                          {item}
                        </motion.li>
                      ))}
                    </ul>
                  </>
                )}
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </div>
  </div>
);

export default TimelineSlide;
